<template>
  <div class="flex-column flex-justify-start flex-align-center">
    <video
      :style="{ width: '720px', height: '480px' }"
      id="video-webrtc"
      ref="videowebrtc"
      controls
      autoplay
      class="mt20"
    ></video>
    <p class="fz24">Live streaming source selection</p>
    <div class="flex-row flex-justify-center flex-align-center">
      <template v-if="liveState && isDockLive">
        <span class="mr10">Lens:</span>
        <a-radio-group v-model:value="lensSelected" button-style="solid">
          <a-radio-button v-for="lens in lensList" :key="lens" :value="lens">{{lens}}</a-radio-button>
        </a-radio-group>
      </template>
    </div>
    <div class="flex-row flex-justify-center flex-align-center">
      <a-select
        style="width:150px"
        placeholder="Select Source"
        @select="onSourceSelect"
        v-model:value="sourceSelected"
      >
        <a-select-option
          v-for="item in sourceList"
          :key="item.value.videoUrl"
          :value="JSON.stringify(item.value)"
          >{{ item.label }}</a-select-option
        >
      </a-select>
      <a-input
        class="ml20"
        v-model:value="liveStreamUrl" placeholder="Url"
      >
      </a-input>
            <a-select
        class="ml10"
        style="width:150px"
        placeholder="Select Clarity"
        @select="onClaritySelect"
        v-model:value="claritySelected"
      >
        <a-select-option
          v-for="item in clarityList"
          :key="item.value"
          :value="item.value"
          >{{ item.label }}</a-select-option
        >
      </a-select>
    </div>
    <div class="flex-row flex-justify-center flex-align-center mt10">
      <a-button class="ml20" v-if="liveState && isDockLive" type="primary" large @click="onSwitch">Switch Lens</a-button>
      <a-button class="ml20" v-else type="primary" large @click="onStart">Play</a-button>
      <a-button class="ml20" type="primary" large @click="onStop">Stop</a-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { message } from 'ant-design-vue'
import { onMounted, reactive, ref, computed } from 'vue'
import { CURRENT_CONFIG as config } from '/@/api/http/config'
import { changeLivestreamLens, getLiveCapacity, setLivestreamQuality, startLivestream, stopLivestream } from '/@/api/manage'
import { getRoot } from '/@/root'
// import jswebrtc from '/@/vendors/jswebrtc.min.js'
// import srs from '/@/vendors/srs.sdk.js'
import store from '../store'

const root = getRoot()

interface SelectOption {
  value: any,
  label: string,
  more?: any
}

const liveTypeList: SelectOption[] = [
  {
    value: 1,
    label: 'RTMP'
  },
  {
    value: 2,
    label: 'RTSP'
  },
  {
    value: 3,
    label: 'GB28181'
  },
  {
    value: 4,
    label: 'WEBRTC'
  }
]

/// TODO: sourceList based on detected devices
const sourceList: SelectOption[] = [
  {
    value: { videoUrl: 'asiacemdrone-cam', deviceSn: '', cameraId: '' },
    label: 'Security Camera'
  },
  {
    value: { videoUrl: 'live/7CTXM9400B01F4-165-0-7', deviceSn: '7CTXM9400B01F4', cameraId: '165-0-7' },
    label: 'Dock Camera'
  },
  {
    value: { videoUrl: 'live/1581F6QAD246400GZD59-80-0-0', deviceSn: '1581F6QAD246400GZD59', cameraId: '80-0-0' },
    label: 'Drone Camera'
  },
]

const clarityList: SelectOption[] = [
  {
    value: 0,
    label: 'Adaptive'
  },
  {
    value: 1,
    label: 'Smooth'
  },
  {
    value: 2,
    label: 'Standard'
  },
  {
    value: 3,
    label: 'HD'
  },
  {
    value: 4,
    label: 'Super Clear'
  }
]

const videowebrtc = ref(null)
const livestreamSource = ref()
const droneList = ref()
const cameraList = ref()
const videoList = ref()
const droneSelected = ref()
const cameraSelected = ref()
const videoSelected = ref()
const claritySelected = ref()
const sourceSelected = ref()
const videoId = ref()
const liveState = ref<boolean>(false)
const livetypeSelected = ref()
const lensList = ref<string[]>([])
const lensSelected = ref<String>()
const isDockLive = ref(false)
const nonSwitchable = 'normal'
let webrtc: any = null

const liveStreamUrl = ref<string>('')

const onRefresh = async () => {
  droneList.value = []
  cameraList.value = []
  videoList.value = []
  droneSelected.value = null
  cameraSelected.value = null
  videoSelected.value = null
  await getLiveCapacity({})
    .then(res => {
      console.log(res)
      if (res.code === 0) {
        if (res.data === null) {
          console.warn('warning: get live capacity is null!!!')
          return
        }
        const resData: Array<[]> = res.data
        console.log('live_capacity:', resData)
        livestreamSource.value = resData

        const temp: Array<SelectOption> = []
        if (livestreamSource.value) {
          livestreamSource.value.forEach((ele: any) => {
            temp.push({ label: ele.name + '-' + ele.sn, value: ele.sn, more: ele.cameras_list })
          })
          droneList.value = temp
        }
      }
    })
    .catch(error => {
      message.error(error)
      console.error(error)
    })
}

onMounted(() => {
  onRefresh()
})

function stopStream () {
  const videoEl:any = document.getElementById('video-webrtc')
  if (!webrtc) return

  // Close all tracks in the video element
  if (videoEl.srcObject) {
    videoEl.srcObject.getTracks().forEach((track:any) => track.stop())
    videoEl.srcObject = null
  }

  // Close the peer connection
  webrtc.getSenders().forEach((sender:any) => sender.track?.stop())
  webrtc.close()

  message.info('Live stream session Stopped!')
}

const startWebRTC = async (videoUrl:string) => {
  const videoEl:any = document.getElementById('video-webrtc')

  // 1️⃣ Create RTCPeerConnection
  stopStream()
  webrtc = null

  webrtc = new RTCPeerConnection()
  // Attach remote tracks to the video element
  webrtc.ontrack = (event:any) => {
    if (!videoEl?.srcObject) videoEl.srcObject = new MediaStream()
    event.streams[0].getTracks().forEach((track:any) => videoEl.srcObject.addTrack(track))
  }

  // 2️⃣ Create SDP offer
  const offer = await webrtc.createOffer({ offerToReceiveVideo: true, offerToReceiveAudio: true })
  await webrtc.setLocalDescription(offer)

  const streamPath = videoUrl + '/whep'
  // 3️⃣ POST offer to MediaMTX WHEP endpoint
  const resp = await fetch(`${streamPath}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/sdp' },
    body: webrtc.localDescription.sdp
  })

  if (!resp.ok) {
    message.error('Failed to create WHEP session ' + resp.status)
    console.error('Failed to create WHEP session:', resp.status, resp.statusText)
    return
  }

  // 4️⃣ Get sessionUrl from Location header
  const sessionUrl = resp.headers.get('Location')
  message.success('Live stream session Started')

  // 5️⃣ Set remote SDP from response body
  const answerSdp = await resp.text()
  if (answerSdp) {
    await webrtc.setRemoteDescription({ type: 'answer', sdp: answerSdp })
  }

  addFrame(videoUrl + '/')
  // 6️⃣ Optional: PATCH later using sessionUrl if you want to renegotiate
  // e.g. fetch(sessionUrl, { method: 'PATCH', headers: {...}, body: newSdp })
}
const onStart = async () => {
  // const streamPath = config.liveSeccamStreamPath // your WHEP endpoint
  const sourceObj = JSON.parse(sourceSelected.value)
  if (sourceObj.videoUrl !== '') {
    if (sourceObj.deviceSn !== '' && sourceObj.cameraId !== '') {
      const timestamp = new Date().getTime().toString()
      const liveURL = config.rtmpURL + timestamp
      const videoId = sourceObj.deviceSn + '/' + sourceObj.cameraId + '/normal-0'
      await startLivestream({
        url: liveURL,
        video_id: videoId,
        url_type: 1,
        video_quality: claritySelected.value ?? 0
      })
    }

    startWebRTC(liveStreamUrl.value).catch(console.error)
  }
}

const onStop = () => {
  const sourceObj = JSON.parse(sourceSelected.value)
  const videoId = sourceObj.deviceSn + '/' + sourceObj.cameraId + '/normal-0'

  stopLivestream({
    video_id: videoId
  }).then(res => {
    if (res.code === 0) {
      message.success(res.message)
      liveState.value = false
      lensSelected.value = undefined
      console.log('stop play livestream')
    }
  })
  stopStream()
}

// const onUpdateQuality = () => {
//   if (!liveState.value) {
//     message.info('Please turn on the livestream first.')
//     return
//   }
//   setLivestreamQuality({
//     video_id: videoId.value,
//     video_quality: sourceSelected.value
//   }).then(res => {
//     if (res.code === 0) {
//       message.success('Set the clarity to ' + sourceList[sourceSelected.value].label)
//     }
//   })
// }

const onLiveTypeSelect = (val: any) => {
  livetypeSelected.value = val
}
const onDroneSelect = (val: SelectOption) => {
  droneSelected.value = val.value
  const temp: Array<SelectOption> = []
  cameraList.value = []
  cameraSelected.value = undefined
  videoSelected.value = undefined
  videoList.value = []
  lensList.value = []
  if (!val.more) {
    return
  }
  val.more.forEach((ele: any) => {
    temp.push({ label: ele.name, value: ele.index, more: ele.videos_list })
  })
  cameraList.value = temp
}
const onCameraSelect = (val: SelectOption) => {
  cameraSelected.value = val.value
  const result: Array<SelectOption> = []
  videoSelected.value = undefined
  videoList.value = []
  lensList.value = []
  if (!val.more) {
    return
  }

  val.more.forEach((ele: any) => {
    result.push({ label: ele.type, value: ele.index, more: ele.switch_video_types })
  })
  videoList.value = result
  if (videoList.value.length === 0) {
    return
  }
  const firstVideo: SelectOption = videoList.value[0]
  videoSelected.value = firstVideo.value
  lensList.value = firstVideo.more
  lensSelected.value = firstVideo.label
  isDockLive.value = lensList.value?.length > 0
}
const onVideoSelect = (val: SelectOption) => {
  videoSelected.value = val.value
  lensList.value = val.more
  lensSelected.value = val.label
}

const onSourceSelect = (val: any) => {
  const sourceObj = JSON.parse(val)
  const baseUrl = config.liveSeccamBaseUrl
  liveStreamUrl.value = baseUrl + '/' + sourceObj.videoUrl
  sourceSelected.value = val
}

const onClaritySelect = (val: any) => {
  claritySelected.value = val
}

const onSwitch = () => {
  if (lensSelected.value === undefined || lensSelected.value === nonSwitchable) {
    message.info('The ' + nonSwitchable + ' lens cannot be switched, please select the lens to be switched.', 8)
    return
  }
  changeLivestreamLens({
    video_id: videoId.value,
    video_type: lensSelected.value
  }).then(res => {
    if (res.code === 0) {
      message.success('Switching live camera successfully.')
    }
  })
}
// const playWebrtc = (videoElement: HTMLMediaElement, url: string) => {
//   if (webrtc) {
//     webrtc.close()
//   }
//   webrtc = new srs.SrsRtcWhipWhepAsync()
//   videoElement.srcObject = webrtc.stream
//   webrtc.play(url).then(function (session: any) {
//     console.info(session)
//   }).catch(function (reason: any) {
//     webrtc.close()
//     console.error(reason)
//   })
// }

const liveStreamFramesZIndexCounter = computed(() => {
  return store.state.liveStreamFramesZIndexCounter
})

const liveStreamFrames = computed(() => {
  return store.state.liveStreamFrames
})

const addFrame = (src:string) => {
  const zindex = liveStreamFramesZIndexCounter.value + 1
  const frames = liveStreamFrames.value
  const frameObj = {
    id: Date.now(),
    src: src,
    zIndex: zindex,
  }
  frames.push(frameObj)
  store.commit('SET_LIVESTREAM_FRAMES_Z_INDEX_COUNTER', zindex)
  store.commit('SET_LIVESTREAM_FRAMES', frames)
}

</script>

<style lang="scss" scoped>
@import '/@/styles/index.scss';
</style>
